import { useEffect, useMemo, useState } from "react";
import { signOut } from "firebase/auth";
import { onValue, push, ref, serverTimestamp, set, update } from "firebase/database";
import {
  Building2,
  ChevronRight,
  LogOut,
  Mail,
  Menu,
  Plus,
  Search,
  Users,
  X
} from "lucide-react";
import { auth, database } from "../firebase";
import { contactMatches, contactMatchesStatus, contactMatchesWebsiteQuality, displayNameForUser, googleRatingTone, groupContactsByStatus, groupContactsByValue, websiteQuality } from "../utils";
import { contactMatchesIndustry, industryForContact, industryLabel, industryOptionsForContacts, normalizeIndustry } from "../industries";
import ContactDetail from "./ContactDetail";
import ContactForm from "./ContactForm";
import TemplatesPanel from "./TemplatesPanel";
import { mergeEmailTemplates } from "../emailTemplates";
import { buildPublicPreview } from "../../../shared/publicPreview.js";

const statusLabels = { new: "Neu", contacted: "Kontaktiert", not_reached: "Nicht erreicht", follow_up: "Follow-up", review: "Nachprüfen", postponed: "Zurückgestellt", interested: "Interessiert", closed: "Abgeschlossen", not_interested: "Kein Interesse" };
const statusOrder = Object.keys(statusLabels);

export default function Workspace({ user }) {
  const [view, setView] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(undefined);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [websiteFilter, setWebsiteFilter] = useState("all");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [grouping, setGrouping] = useState("none");
  const [formContact, setFormContact] = useState(undefined);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    set(ref(database, `admin/users/${user.uid}`), {
      displayName: displayNameForUser(user),
      email: user.email.toLowerCase(),
      lastSeenAt: serverTimestamp()
    });
    const stopContacts = onValue(ref(database, "admin/contacts"), (snapshot) => {
      const value = snapshot.val() || {};
      const next = Object.entries(value).map(([id, item]) => ({ id, ...item })).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
      setContacts(next);
      setSelectedId((current) => {
        if (current === undefined) return window.matchMedia("(max-width: 700px)").matches ? null : next[0]?.id || null;
        return current && next.some((item) => item.id === current) ? current : null;
      });
    });
    const stopTemplates = onValue(ref(database, "admin/emailTemplates"), (snapshot) => {
      const value = snapshot.val() || {};
      setTemplates(Object.entries(value).map(([id, item]) => ({ id, ...item })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    });
    return () => { stopContacts(); stopTemplates(); };
  }, [user]);

  const industryOptions = useMemo(() => industryOptionsForContacts(contacts), [contacts]);
  const shownContacts = useMemo(() => contacts.filter((contact) => contactMatches(contact, search) && contactMatchesStatus(contact, statusFilter) && contactMatchesWebsiteQuality(contact, websiteFilter) && contactMatchesIndustry(contact, industryFilter)), [contacts, search, statusFilter, websiteFilter, industryFilter]);
  const contactGroups = useMemo(() => {
    if (grouping === "status") return groupContactsByStatus(shownContacts, statusOrder).map((group) => ({ key: `status:${group.status}`, label: statusLabels[group.status], className: `status-${group.status}`, contacts: group.contacts }));
    if (grouping === "website") return [
      { key: "website:poor", label: "Keine/Schlechte Website", className: "website-poor", contacts: shownContacts.filter((contact) => websiteQuality(contact.website) === "poor") },
      { key: "website:secure", label: "SSL-Website vorhanden", className: "website-secure", contacts: shownContacts.filter((contact) => websiteQuality(contact.website) === "secure") }
    ].filter((group) => group.contacts.length > 0);
    if (grouping === "city") return groupContactsByValue(shownContacts, (contact) => contact.address?.city, "Ohne Stadt").map((group) => ({ ...group, className: "location-group" }));
    if (grouping === "state") return groupContactsByValue(shownContacts, (contact) => contact.address?.state, "Ohne Bundesland / Region").map((group) => ({ ...group, className: "location-group" }));
    if (grouping === "industry") return groupContactsByValue(shownContacts, (contact) => industryLabel(industryForContact(contact)), "Ohne Branche").map((group) => ({ ...group, className: "industry-group" }));
    return [{ key: "all", label: "Kontakte", className: "", contacts: shownContacts }];
  }, [grouping, shownContacts]);
  const poorWebsiteCount = useMemo(() => contacts.filter((contact) => websiteQuality(contact.website) === "poor").length, [contacts]);
  const selected = contacts.find((contact) => contact.id === selectedId) || null;
  const usableTemplates = mergeEmailTemplates(templates);

  useEffect(() => {
    if (selectedId && !shownContacts.some((contact) => contact.id === selectedId)) {
      setSelectedId(window.matchMedia("(max-width: 700px)").matches ? null : shownContacts[0]?.id || null);
    }
  }, [selectedId, shownContacts]);

  async function saveContact(form) {
    const contactId = formContact?.id || push(ref(database, "admin/contacts")).key;
    const addressParts = [form.line1, form.line2, [form.city, form.state].filter(Boolean).join(", "), form.postalCode].filter(Boolean);
    const createdAt = formContact?.createdAt || serverTimestamp();
    const contact = {
      companyName: form.companyName,
      contactName: form.contactName,
      contactRole: form.contactRole,
      contactSource: form.contactSource,
      email: form.email,
      emailSource: form.emailSource,
      website: form.website,
      websiteStatus: form.websiteStatus,
      websiteQuality: form.websiteQuality,
      websiteNotes: form.websiteNotes.trim(),
      leadSource: form.leadSource.trim(),
      industry: normalizeIndustry(form.industry),
      campaign: form.campaign.trim(),
      country: form.country.trim().toUpperCase(),
      enrichmentStatus: form.enrichmentStatus,
      phone: form.phone,
      address: {
        line1: form.line1.trim(),
        line2: form.line2.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
        countryCode: form.countryCode,
        formatted: addressParts.join(", ")
      },
      googleMapsUrl: form.googleMapsUrl.trim(),
      googlePlaceId: form.googlePlaceId,
      previewEnabled: form.previewEnabled,
      status: form.status,
      search: {
        companyNameLower: form.companyName.toLowerCase(),
        contactNameLower: form.contactName.toLowerCase(),
        emailLower: form.email.toLowerCase(),
        cityLower: form.city.toLowerCase(),
        phoneDigits: form.phone.e164.replace(/\D/g, "")
      },
      createdAt,
      createdBy: formContact?.createdBy || user.uid,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    };
    if (Number.isFinite(form.googleRating)) {
      contact.googleRating = form.googleRating;
      contact.googleRatingCount = Number.isFinite(form.googleRatingCount) ? form.googleRatingCount : 0;
    }
    const publicPreview = buildPublicPreview(contact, serverTimestamp());
    await update(ref(database), {
      [`admin/contacts/${contactId}`]: contact,
      [`publicPreviews/${contactId}`]: publicPreview
    });
    setSelectedId(contactId);
    setFormContact(undefined);
  }

  async function deleteContact(contactId) {
    await update(ref(database), {
      [`admin/contacts/${contactId}`]: null,
      [`admin/contactNotes/${contactId}`]: null,
      [`publicPreviews/${contactId}`]: null
    });
    setSelectedId(null);
  }

  const nav = (
    <>
      <div className="sidebar-brand"><div className="brand-monogram">MG</div><div><strong>MarkenGroup</strong><span>LeadMagnet</span></div></div>
      <nav>
        <button className={view === "contacts" ? "active" : ""} onClick={() => { setView("contacts"); setMenuOpen(false); }}><Users /> Kontakte <span>{contacts.length}</span></button>
        <button className={view === "templates" ? "active" : ""} onClick={() => { setView("templates"); setMenuOpen(false); }}><Mail /> E-Mail-Vorlagen</button>
      </nav>
      <div className="sidebar-foot"><div className="user-avatar">{displayNameForUser(user).split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()}</div><div><strong>{displayNameForUser(user)}</strong><span>{user.email}</span></div><button onClick={() => signOut(auth)} aria-label="Abmelden"><LogOut /></button></div>
    </>
  );

  return (
    <div className="app-shell">
      <aside className="sidebar">{nav}</aside>
      {menuOpen && <div className="mobile-sidebar"><button className="mobile-close" onClick={() => setMenuOpen(false)}><X /></button>{nav}</div>}
      <header className="mobile-header"><button className="icon-button" onClick={() => setMenuOpen(true)}><Menu /></button><strong>LeadMagnet</strong><div className="user-avatar small">{displayNameForUser(user)[0]}</div></header>

      <div className="workspace">
        {view === "templates" ? <TemplatesPanel templates={templates} user={user} /> : <>
          <aside className="contact-column">
            <header className="contact-column-head"><div><p className="eyebrow">Pipeline</p><h1>Kontakte</h1></div><button className="button primary square" onClick={() => setFormContact(null)} aria-label="Kontakt hinzufügen"><Plus /></button></header>
            <label className="search-box"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, E-Mail, Telefon oder Stadt" /></label>
            <div className="contact-tools">
              <label className="contact-tool"><span>Status filtern</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">Alle Status ({contacts.length})</option>{statusOrder.map((status) => <option key={status} value={status}>{statusLabels[status]} ({contacts.filter((contact) => contact.status === status).length})</option>)}</select></label>
              <label className="contact-tool"><span>Website filtern</span><select value={websiteFilter} onChange={(event) => setWebsiteFilter(event.target.value)}><option value="all">Alle Websites ({contacts.length})</option><option value="poor">Keine/Schlechte ({poorWebsiteCount})</option><option value="secure">SSL vorhanden ({contacts.length - poorWebsiteCount})</option></select></label>
              <label className="contact-tool"><span>Branche filtern</span><select value={industryFilter} onChange={(event) => setIndustryFilter(event.target.value)}><option value="all">Alle Branchen ({contacts.length})</option>{industryOptions.map(({ value, label }) => <option key={value} value={value}>{label} ({contacts.filter((contact) => industryForContact(contact) === value).length})</option>)}</select></label>
              <label className="contact-tool"><span>Gruppieren</span><select value={grouping} onChange={(event) => setGrouping(event.target.value)}><option value="none">Keine Gruppierung</option><option value="industry">Nach Branche</option><option value="status">Nach Status</option><option value="website">Nach Websitequalität</option><option value="city">Nach Stadt</option><option value="state">Nach Bundesland / Region</option></select></label>
            </div>
            <div className="result-summary"><span>{shownContacts.length} {shownContacts.length === 1 ? "Kontakt" : "Kontakte"}{grouping !== "none" && shownContacts.length > 0 ? ` · ${contactGroups.length} ${contactGroups.length === 1 ? "Gruppe" : "Gruppen"}` : ""}</span>{(search || statusFilter !== "all" || websiteFilter !== "all" || industryFilter !== "all") && <button onClick={() => { setSearch(""); setStatusFilter("all"); setWebsiteFilter("all"); setIndustryFilter("all"); }}>Filter löschen</button>}</div>
            <div className="contact-list">
              {contactGroups.map((group) => <section className="contact-group" key={group.key} aria-label={group.label}>
                {grouping !== "none" && <div className={`contact-group-heading ${group.className}`}><span>{group.label}</span><b>{group.contacts.length}</b></div>}
                {group.contacts.map((contact) => {
                  const poorWebsite = websiteQuality(contact.website) === "poor";
                  return <button key={contact.id} className={`contact-row ${poorWebsite ? "poor-website" : ""} ${selectedId === contact.id ? "active" : ""}`} onClick={() => setSelectedId(contact.id)}>
                    <div className="company-avatar small">{contact.companyName?.slice(0, 2).toUpperCase()}</div>
                    <div><strong>{contact.companyName}</strong><span>{contact.contactName || contact.address?.city || "Ohne Ansprechpartner"}</span><div className="contact-row-meta"><small className="industry-tag">{industryLabel(industryForContact(contact))}</small><small className={`status-dot status-${contact.status}`}>{statusLabels[contact.status] || "Neu"}</small>{poorWebsite && <small className="website-quality-tag" title={contact.website ? "Website verwendet kein HTTPS" : "Keine Website hinterlegt"}>Keine/Schlechte Website</small>}{Number.isFinite(contact.googleRating) && <small className={`contact-rating rating-${googleRatingTone(contact.googleRating)}`} aria-label={`${contact.googleRating.toFixed(1)} von 5 Sternen bei ${(contact.googleRatingCount || 0).toLocaleString("de-DE")} Google-Bewertungen`}><span className="contact-stars" style={{ "--rating-width": `${Math.max(0, Math.min(100, contact.googleRating / 5 * 100))}%` }} aria-hidden="true">★★★★★</span><b>{contact.googleRating.toFixed(1)}</b>{contact.googleRatingCount > 0 && <span className="contact-rating-count">({contact.googleRatingCount.toLocaleString("de-DE")})</span>}</small>}</div></div>
                    <ChevronRight />
                  </button>;
                })}
              </section>)}
              {shownContacts.length === 0 && <div className="empty-state"><Search /><strong>{contacts.length ? "Keine Treffer" : "Noch keine Kontakte"}</strong><p>{contacts.length ? "Versuche einen anderen Suchbegriff." : "Lege den ersten Prospect für deine Anrufliste an."}</p>{!contacts.length && <button className="button primary" onClick={() => setFormContact(null)}><Plus /> Kontakt hinzufügen</button>}</div>}
            </div>
          </aside>
          <main className="content-column">
            {selected ? <ContactDetail contact={selected} user={user} templates={usableTemplates} onBack={() => setSelectedId(null)} onEdit={() => setFormContact(selected)} onDelete={deleteContact} /> : <div className="empty-detail"><div className="brand-orb"><Building2 /></div><h2>Kontakt auswählen</h2><p>Kontaktdaten, Vorschau und Gesprächsnotizen erscheinen hier.</p></div>}
          </main>
        </>}
      </div>
      {formContact !== undefined && <ContactForm contact={formContact} onClose={() => setFormContact(undefined)} onSave={saveContact} />}
    </div>
  );
}
